import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { DataService } from '../dataService/data.service';
import { GlobalVariable } from '../app.component';

@Injectable({
  providedIn: 'root'
})
export class ReviewSetupCacheService {
  private cache = new Map<string, Observable<any>>();
  private cacheRefresh$ = new Subject<string>();
  private BASE_URL = GlobalVariable;

  constructor(private httpService: DataService) {}

  /**
   * Get review setup value with caching to prevent multiple simultaneous API calls
   * @param outletId - The outlet ID
   * @param forceRefresh - Force a fresh API call, bypassing cache
   */
  getReviewSetupValue(outletId: string, forceRefresh: boolean = false): Observable<any> {
    const cacheKey = `review_setup_${outletId}`;

    // Clear cache if force refresh
    if (forceRefresh && this.cache.has(cacheKey)) {
      this.cache.delete(cacheKey);
    }

    // Return cached observable if exists
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Create new request with shareReplay to prevent duplicate calls
    const request$ = this.httpService
      .get(this.BASE_URL.REVIEW_SETUP_VALUE_URL_V2(outletId))
      .pipe(
        shareReplay({
          bufferSize: 1,
          refCount: false
        }),
        tap(() => {
          // Cache expires after 5 seconds to allow fresh data
          setTimeout(() => {
            this.cache.delete(cacheKey);
          }, 5000);
        })
      );

    this.cache.set(cacheKey, request$);
    return request$;
  }

  /**
   * Clear cache for a specific outlet
   */
  clearCache(outletId: string): void {
    this.cache.delete(`review_setup_${outletId}`);
    this.cacheRefresh$.next(outletId);
  }

  /**
   * Clear all cache
   */
  clearAllCache(): void {
    this.cache.clear();
  }

  /**
   * Observable to listen for cache refresh events
   */
  onCacheRefresh(): Observable<string> {
    return this.cacheRefresh$.asObservable();
  }
}
